import React from 'react';
import {
  EMPTY_PRETEXT_DRAFT,
  PRETEXT_CHANNELS,
  PRETEXT_WIZARD_SECTIONS,
  pretextToDraft,
} from '../constants';
import { tUi } from '../utils/uiText';

const LARGE_FIELDS = new Set([
  'supporting_legend',
  'opening_hook',
  'objective_language',
  'conversation_waypoints',
  'resistance_responses',
  'script',
  'supporting_details',
]);

function previewText(pretext, uiLanguage) {
  return (
    pretext.opening_hook ||
    pretext.objective_language ||
    pretext.script ||
    pretext.primary_objective ||
    tUi(uiLanguage, 'pretextNoContentYet')
  );
}

export default function PretextStudio({
  uiLanguage,
  pretexts,
  loading,
  onRefresh,
  onCreate,
  onUpdate,
  onDelete,
  onExportPack,
  onExtractFromChat,
  extracting,
  extractFeedback,
}) {
  const [draft, setDraft] = React.useState(EMPTY_PRETEXT_DRAFT);
  const [editingId, setEditingId] = React.useState(null);
  const [activeSection, setActiveSection] = React.useState(PRETEXT_WIZARD_SECTIONS[0].id);
  const [showForm, setShowForm] = React.useState(false);

  const startEdit = (pretext) => {
    setEditingId(pretext.id);
    setDraft(pretextToDraft(pretext));
    setActiveSection(PRETEXT_WIZARD_SECTIONS[0].id);
    setShowForm(true);
  };

  const resetDraft = () => {
    setEditingId(null);
    setDraft(EMPTY_PRETEXT_DRAFT);
    setActiveSection(PRETEXT_WIZARD_SECTIONS[0].id);
    setShowForm(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      ...draft,
      objective: draft.primary_objective || draft.objective,
      script: draft.objective_language || draft.script,
      tags: draft.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
    };
    try {
      if (editingId) {
        await onUpdate(editingId, payload);
      } else {
        await onCreate(payload);
      }
      resetDraft();
    } catch (err) {
      console.error('Failed to save pretext:', err);
    }
  };

  const updateField = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const currentSection =
    PRETEXT_WIZARD_SECTIONS.find((section) => section.id === activeSection) ||
    PRETEXT_WIZARD_SECTIONS[0];

  return (
    <div className="mb-4 bg-gray-900 rounded-lg border border-teal-500/30 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-teal-400">{tUi(uiLanguage, 'pretextStudioTitle')}</h3>
          <p className="text-sm text-gray-400">
            {tUi(uiLanguage, 'pretextStudioDescription')}
            {pretexts.length > 0 && (
              <span className="ml-2 text-teal-300">
                {tUi(uiLanguage, 'pretextSavedCount', { count: pretexts.length })}
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onRefresh()}
            disabled={loading}
            className="px-3 py-2 text-sm bg-gray-800 hover:bg-gray-700 border border-teal-500/30 rounded-lg text-white"
          >
            {tUi(uiLanguage, 'commonRefresh')}
          </button>
          <div className="flex flex-col items-end gap-0.5">
            <button
              type="button"
              onClick={onExtractFromChat}
              disabled={extracting}
              className="px-3 py-2 text-sm bg-gray-800 hover:bg-gray-700 border border-teal-500/30 rounded-lg text-white"
            >
              {extracting ? tUi(uiLanguage, 'pretextExtracting') : tUi(uiLanguage, 'pretextExtractFromChat')}
            </button>
            <span className="text-xs text-gray-500 max-w-[14rem] text-right">
              {tUi(uiLanguage, 'pretextExtractHint')}
            </span>
          </div>
          <button
            type="button"
            onClick={onExportPack}
            disabled={!pretexts.length}
            className="px-3 py-2 text-sm bg-teal-500 hover:bg-teal-400 disabled:bg-gray-700 text-black font-semibold rounded-lg"
          >
            {tUi(uiLanguage, 'pretextExportCampaignPack')}
          </button>
          {!showForm && (
            <button
              type="button"
              onClick={() => {
                resetDraft();
                setShowForm(true);
              }}
              className="px-3 py-2 text-sm bg-teal-600 hover:bg-teal-500 text-black font-semibold rounded-lg"
            >
              {tUi(uiLanguage, 'pretextNew')}
            </button>
          )}
        </div>
      </div>

      {extractFeedback && (
        <p className="mb-4 text-sm text-teal-200/90 bg-teal-950/30 border border-teal-500/30 rounded-lg px-3 py-2">
          {extractFeedback}
        </p>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-4 border border-teal-500/20 rounded-lg p-4 bg-gray-950">
          <div className="grid gap-3 md:grid-cols-2 mb-4">
            <input
              value={draft.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder={tUi(uiLanguage, 'pretextNamePlaceholder')}
              required
              className="px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
            />
            <input
              value={draft.target_audience}
              onChange={(e) => updateField('target_audience', e.target.value)}
              placeholder={tUi(uiLanguage, 'pretextTargetAudiencePlaceholder')}
              className="px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
            />
            <input
              value={draft.tags}
              onChange={(e) => updateField('tags', e.target.value)}
              placeholder={tUi(uiLanguage, 'pretextTagsPlaceholder')}
              className="px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white md:col-span-2"
            />
          </div>

          <div className="flex flex-wrap gap-1 mb-4">
            {PRETEXT_WIZARD_SECTIONS.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveSection(section.id)}
                className={`px-2 py-1 text-xs rounded border ${
                  activeSection === section.id
                    ? 'bg-teal-500 text-black border-teal-400 font-semibold'
                    : 'bg-gray-800 text-gray-300 border-teal-500/20 hover:border-teal-500/40'
                }`}
              >
                {section.title.replace(/^\d+\.\s*/, '')}
              </button>
            ))}
          </div>

          <div className="space-y-3 mb-4">
            <h4 className="text-sm font-semibold text-teal-300">{currentSection.title}</h4>
            {currentSection.fields.map(([field, label]) => {
              if (field === 'channel') {
                return (
                  <label key={field} className="block text-sm text-gray-300">
                    <span className="block mb-1">{label}</span>
                    <select
                      value={draft.channel}
                      onChange={(e) => updateField('channel', e.target.value)}
                      className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
                    >
                      {PRETEXT_CHANNELS.map((channel) => (
                        <option key={channel} value={channel}>
                          {channel}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              }

              const rows = LARGE_FIELDS.has(field) ? 5 : 3;
              return (
                <label key={field} className="block text-sm text-gray-300">
                  <span className="block mb-1">{label}</span>
                  <textarea
                    value={draft[field] || ''}
                    onChange={(e) => updateField(field, e.target.value)}
                    rows={rows}
                    className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
                  />
                </label>
              );
            })}
          </div>

          <details className="mb-4 text-sm text-gray-400">
            <summary className="cursor-pointer text-teal-400 hover:text-teal-300">
              {tUi(uiLanguage, 'pretextLegacyFields')}
            </summary>
            <div className="mt-3 space-y-3">
              <textarea
                value={draft.script}
                onChange={(e) => updateField('script', e.target.value)}
                placeholder={tUi(uiLanguage, 'pretextLegacyScriptPlaceholder')}
                rows={4}
                className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
              />
              <textarea
                value={draft.supporting_details}
                onChange={(e) => updateField('supporting_details', e.target.value)}
                placeholder={tUi(uiLanguage, 'pretextAdditionalNotesPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
              />
            </div>
          </details>

          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-teal-500 hover:bg-teal-400 text-black font-semibold rounded-lg"
            >
              {editingId ? tUi(uiLanguage, 'pretextUpdate') : tUi(uiLanguage, 'pretextAdd')}
            </button>
            <button
              type="button"
              onClick={resetDraft}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
            >
              {tUi(uiLanguage, 'commonCancel')}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {pretexts.length === 0 && (
          <p className="text-sm text-gray-500">
            {tUi(uiLanguage, 'pretextEmptyStateBeforeExtract')}
            {' '}
            <strong className="text-teal-300">{tUi(uiLanguage, 'pretextExtractFromChat')}</strong>
            {' '}
            {tUi(uiLanguage, 'pretextEmptyStateMiddle')}
            {' '}
            <strong className="text-teal-300">{tUi(uiLanguage, 'pretextNew')}</strong>
            {' '}
            {tUi(uiLanguage, 'pretextEmptyStateAfter')}
          </p>
        )}
        {pretexts.map((pretext) => (
          <div key={pretext.id} className="bg-gray-800 border border-teal-500/20 rounded-lg p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <h4 className="font-semibold text-white">{pretext.name}</h4>
                <p className="text-xs text-gray-400">
                  {pretext.channel}
                  {' · '}
                  {pretext.persona_name || tUi(uiLanguage, 'pretextNoPersona')}
                  {' · v'}
                  {pretext.version || 1}
                  {' · '}
                  {pretext.source || tUi(uiLanguage, 'pretextSourceManual')}
                </p>
                {pretext.primary_objective && (
                  <p className="text-xs text-teal-300/80 mt-1">{pretext.primary_objective}</p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => startEdit(pretext)}
                  className="text-sm text-teal-400 hover:text-teal-300"
                >
                  {tUi(uiLanguage, 'commonEdit')}
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(pretext.id)}
                  className="text-sm text-red-400 hover:text-red-300"
                >
                  {tUi(uiLanguage, 'commonDelete')}
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-300 whitespace-pre-wrap line-clamp-4">
              {previewText(pretext, uiLanguage)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
